import { tokenStorage } from 'core/network/tokenStorage'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AdminNotificationItem, notificationsApi } from '../data/notificationsApi'

let sharedAudioCtx: AudioContext | null = null

const getAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null

  if (!sharedAudioCtx) {
    const AudioContextClass =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext

    if (AudioContextClass) {
      sharedAudioCtx = new AudioContextClass()
    }
  }

  return sharedAudioCtx
}

// Unlocks AudioContext and requests Notification permissions on the first user interaction
const unlockAudioAndPermissions = () => {
  try {
    const ctx = getAudioContext()

    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {})
    }

    if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {})
    }
  } catch {
    // Ignore browser policy exceptions
  }
}

const playNotificationSound = () => {
  try {
    const ctx = getAudioContext()
    if (!ctx || ctx.state !== 'running') return

    const osc1 = ctx.createOscillator()
    const gain1 = ctx.createGain()
    osc1.type = 'sine'
    osc1.frequency.setValueAtTime(587.33, ctx.currentTime)
    gain1.gain.setValueAtTime(0.12, ctx.currentTime)
    gain1.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25)
    osc1.connect(gain1)
    gain1.connect(ctx.destination)
    osc1.start(ctx.currentTime)
    osc1.stop(ctx.currentTime + 0.25)

    const osc2 = ctx.createOscillator()
    const gain2 = ctx.createGain()
    osc2.type = 'sine'
    osc2.frequency.setValueAtTime(880, ctx.currentTime + 0.08)
    gain2.gain.setValueAtTime(0.12, ctx.currentTime + 0.08)
    gain2.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4)
    osc2.connect(gain2)
    gain2.connect(ctx.destination)
    osc2.start(ctx.currentTime + 0.08)
    osc2.stop(ctx.currentTime + 0.4)
  } catch {
    // Ignore audio policy
  }
}

const triggerBrowserNotification = (notification: AdminNotificationItem, onNavigate?: (url: string) => void) => {
  if (typeof window === 'undefined' || !('Notification' in window)) return

  // Only trigger if permission was already explicitly granted by user gesture
  if (Notification.permission === 'granted') {
    const n = new Notification(notification.title, {
      body: notification.message,
      icon: '/favicon.ico',
    })

    n.onclick = () => {
      window.focus()

      if (notification.action_url && onNavigate) {
        onNavigate(notification.action_url)
      }

      n.close()
    }
  }
}

export const useAdminNotifications = () => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState<AdminNotificationItem[]>([])
  const [toastStack, setToastStack] = useState<AdminNotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [isConnected, setIsConnected] = useState<boolean>(false)
  const eventSourceRef = useRef<EventSource | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const dismissToast = useCallback((id: string) => {
    setToastStack((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const dismissAllToasts = useCallback(() => {
    setToastStack([])
  }, [])

  const fetchInitialData = useCallback(async () => {
    try {
      setLoading(true)
      const [listRes, count] = await Promise.all([
        notificationsApi.getNotifications({ limit: 20 }),
        notificationsApi.getUnreadCount(),
      ])

      setNotifications(listRes.data || [])
      setUnreadCount(count || 0)
    } catch (err) {
      // NOSONAR: Logging initial notification loading failure for client diagnostics
      // eslint-disable-next-line no-console
      console.error('Failed to load initial notifications:', err) // NOSONAR
    } finally {
      setLoading(false)
    }
  }, [])

  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationsApi.markAsRead(id)
      setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)))
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (err) {
      // NOSONAR: Logging mark as read error for client diagnostics
      // eslint-disable-next-line no-console
      console.error('Failed to mark notification as read:', err) // NOSONAR
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsApi.markAllAsRead()
      setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })))
      setUnreadCount(0)
    } catch (err) {
      // NOSONAR: Logging mark all as read error for client diagnostics
      // eslint-disable-next-line no-console
      console.error('Failed to mark all notifications as read:', err) // NOSONAR
    }
  }, [])

  const handleIncomingNotification = useCallback(
    (e: MessageEvent) => {
      try {
        if (!e.data || typeof e.data !== 'string' || e.data.trim().startsWith(':')) {
          return
        }

        const newNotif: AdminNotificationItem = JSON.parse(e.data)

        setNotifications((prev) => {
          if (prev.some((item) => item.id === newNotif.id)) {
            return prev
          }

          return [newNotif, ...prev]
        })
        setUnreadCount((prev) => prev + 1)
        setToastStack((prev) => [newNotif, ...prev.filter((i) => i.id !== newNotif.id)].slice(0, 20))

        playNotificationSound()
        triggerBrowserNotification(newNotif, (url) => navigate(url))
      } catch (err) {
        // NOSONAR: Logging incoming notification parse error for diagnostics
        // eslint-disable-next-line no-console
        console.error('Failed to parse incoming notification event:', err) // NOSONAR
      }
    },
    [navigate],
  )

  useEffect(() => {
    fetchInitialData()

    // Register user gesture listener to unlock audio context and request notification permission
    window.addEventListener('click', unlockAudioAndPermissions, { once: true, passive: true })
    window.addEventListener('keydown', unlockAudioAndPermissions, { once: true, passive: true })
    window.addEventListener('touchstart', unlockAudioAndPermissions, { once: true, passive: true })

    const connectSSE = () => {
      const token = tokenStorage.getAccessToken()

      if (!token) return

      const baseUrl = process.env.REACT_APP_BASE_URL || ''
      const streamUrl = `${baseUrl}/api/v1/notifications/stream?token=${encodeURIComponent(token)}`

      const es = new EventSource(streamUrl)

      eventSourceRef.current = es

      es.addEventListener('connected', () => {
        setIsConnected(true)
      })

      es.addEventListener('notification', handleIncomingNotification)

      es.onerror = () => {
        setIsConnected(false)
        es.close()

        // Try reconnecting after 5 seconds
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current)

        reconnectTimeoutRef.current = setTimeout(() => {
          connectSSE()
        }, 5000)
      }
    }

    connectSSE()

    return () => {
      window.removeEventListener('click', unlockAudioAndPermissions)
      window.removeEventListener('keydown', unlockAudioAndPermissions)
      window.removeEventListener('touchstart', unlockAudioAndPermissions)

      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [fetchInitialData, handleIncomingNotification])

  return {
    notifications,
    toastStack,
    unreadCount,
    loading,
    isConnected,
    markAsRead,
    markAllAsRead,
    dismissToast,
    dismissAllToasts,
    refetch: fetchInitialData,
  }
}
